import {
  ActivityKindOption,
  BaseDictOption,
  CountryDictOption,
  RegionDictOption,
  RoleTypeDictOption,
} from 'api/dict/params';
import { UserData } from 'components/UserDataContext/types';

export type DateRange = {
  beginDate?: string;
  endDate?: string;
};

export type Interval<T = string> = {
  from?: T;
  to?: T;
};

/** makes all fields of T nullable */
export type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

export enum AnimalSex {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  MIXED = 'MIXED',
}

export enum AnimalOrganSystem {
  NERVOUS = 'NERVOUS',
  CARDIOVASCULAR = 'CARDIOVASCULAR',
  EXCRETORY = 'EXCRETORY',
  RESPIRATORY = 'RESPIRATORY',
  DIGESTIVE = 'DIGESTIVE',
  REPRODUCTIVE = 'REPRODUCTIVE',
  SKELETAL = 'SKELETAL',
  IMMUNE = 'IMMUNE',
  INBORN_ERRORS = 'INBORN_ERRORS',
  OTHER = 'OTHER',
}

export enum Founder {
  LEGAL = 'LEGAL',
  PHYSICAL = 'PHYSICAL',
}

export enum CerberusBusinessEntityType {
  JURIDICAL = 'JURIDICAL',
  PHYSICAL = 'PHYSICAL',
  SELF_EMPLOYED = 'SELF_EMPLOYED',
}

export enum CerberusIntegrationStatus {
  ENTERED_MANUALLY = 'ENTERED_MANUALLY',
  NOT_LINKED_WITH_CERBERUS = 'NOT_LINKED_WITH_CERBERUS',
}

export type CerberusBusinessEntityTypeOption = {
  value: CerberusBusinessEntityType;
  label: string;
};

export enum IntegrationObjectStatus {
  NOT_TRIED_TO_LOAD = 'NOT_TRIED_TO_LOAD',
  EXTERNAL_SYSTEM_ERROR = 'EXTERNAL_SYSTEM_ERROR',
  NOT_FOUND = 'NOT_FOUND',
  LOADED = 'LOADED',
  NOT_LINKED = 'NOT_LINKED',
  BUSINESS_ENTITY_GUID_OUTDATED = 'BUSINESS_ENTITY_GUID_OUTDATED',
}

export type FileType = {
  contentType: string;
  id: string;
  name: string;
  ref: string;
  createDate?: string;
};

export type RegistryProductFile = {
  id?: string;
  name: string;
  language: BaseDictOption;
  file: FileType;
};

export type UploadedFiles = {
  uploadedFiles?: FileType[];
};

export type BusinessEntityWrapper = {
  id: string;
  name: string;
  regNumber: string;
  shortName: string;
  mainAddress: Address;
  country: CountryDictOption;
  businessEntity: BusinessEntity;
  addressList: Address[];
  users: UserData[];
  isNotUsed: boolean;
  address: string;
  holder: boolean;
  vendor: boolean;
  patentee: boolean;
  producer: boolean;
  supplier: boolean;
  resident: boolean;
  confirmed: boolean;
  cerberusSync: boolean;
  cerberusSyncDate: string;
  businessEntityLoadingStatus: IntegrationObjectStatus;
  integrationStatus: IntegrationObjectStatus;
};

export type Address = {
  id: string;
  address: string;
};

export type IkarAddress = {
  country?: CountryDictOption;
  region?: RegionDictOption;
  district?: RegionDictOption;
  locality?: CountryDictOption;
  subLocality?: CountryDictOption;
  street?: CountryDictOption;
  house: string;
  building?: string;
  room?: string;
  postIndex?: string;
  postBox?: string;
  addressView?: string;
  integrationStatus?: IntegrationObjectStatus;
};

export type FullAddress = Address & {
  businessEntity: BusinessEntityWrapper;
};

export type BusinessEntity = {
  guid: string;
  fio: string;
  firmName: string;
  firmNameShort: string;
  passport: string;
  inn: string;
  kpp: string;
  ogrn: string;
  juridicalAddress: IkarAddress;
  type: CerberusBusinessEntityType;
  integrationStatus: IntegrationObjectStatus;
};

export type Producer = {
  id?: string;
  name: string;
  address?: FullAddress;
  addressText?: string;
  country?: CountryDictOption;
  resident: boolean;
  notLinkedWithCerberus?: boolean;
  enteredManually?: boolean;
  incorrectCerberusLink?: boolean;
};

export type User = {
  id: string;
  fio: string;
};

export type ActivityLocationDTO = {
  activityLocation?: BusinessEntity | null;
  activityLocationName: string;
  activityLocationAddress: string;
  id?: string | null;
  resident: string | boolean | null;
  country?: CountryDictOption;
};

export type SupervisedObjectType = {
  guid: string;
  name: string;
};

export type SupervisedObject = {
  guid?: string;
  id?: string;
  name?: string;
  regNumber?: string;
  activityLocation?: {
    address?: string;
    country?: CountryDictOption;
    guid?: string;
    id?: string;
    name?: string;
    regNumber?: string;
    region?: RegionDictOption;
  };
  roleTypeList?: RoleTypeDictOption[];
  businessEntityWrapper?: BusinessEntityWrapper;
  supervisedObjectType?: SupervisedObjectType;
  supervisedObjectActivityTypeList?: ActivityKindOption[];
};

export type SupervisedObjectWrapper = {
  id: string;
  supervisedObject: SupervisedObject;
};
